import { z } from 'zod';
import type { QueryClient } from '@tanstack/react-query';
import { HTTPError } from 'ky';
import { salesSummaryQuery } from '@/api/queries';
import { getSession } from '@/api/session';
import type { ToolConfig } from './utils/create-tool';

const inputSchema = z.object({
  from_date: z.string(),
  to_date: z.string(),
  loc_ids: z.array(z.number()).optional(),
});

async function execute(params: z.infer<typeof inputSchema>, queryClient: QueryClient) {
  const { from_date, to_date, loc_ids } = params;
  const session = getSession();
  const locIds = loc_ids ?? (session?.LocId ? [session.LocId] : []);

  try {
    const data = await queryClient.fetchQuery(salesSummaryQuery(locIds, from_date, to_date));

    return {
      date_range: { from: from_date, to: to_date },
      locations: locIds,
      summary: data,
    };
  } catch (error) {
    if (error instanceof HTTPError && error.response.status === 403) {
      return {
        error:
          'Permission denied: ViewMLSR permission is required to access sales summary reports. Please contact your administrator to enable this permission.',
        date_range: { from: from_date, to: to_date },
        locations: locIds,
      };
    }
    throw error;
  }
}

export const salesSummaryTool: ToolConfig<typeof inputSchema> = {
  name: 'get_sales_summary',
  description:
    'Retrieves aggregated sales summary data from Snowflake. Provides high-level metrics like total revenue, units sold, and breakdowns by category or brand. Use this for broad trend analysis, overstock detection, or when the user asks about sales performance.',
  inputSchema,
  execute,
};
